import React, { useRef } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import AotdShiftBanner from "../AotdShiftBanner";
import AotdArtCard from "../AotdArtCard";
import EmojiPenaltyNote from "../shared/EmojiPenaltyNote";
import RaceButton from "../shared/RaceButton";
import AotdCardsReadyButton from "../../../respectCards/aotd/AotdCardsReadyButton";
import type { RaceClock } from "../../hooks/useRaceClock";
import type { ArtInfo, EmojiPenaltyResponse } from "../../api/types";

const SignupMessage = styled.div`
  font-size: 1rem;
  font-weight: 900;
  color: #323232;
  text-align: center;
  margin-top: 2rem;
`;

/**
 * `not_started` + scheduledTime branch: shows the orange shift banner and
 * the previous AOTD as a read-only art card (if available), or a small
 * "check back later" message. The race button sits under the banner and
 * turns green at unlock by the server's clock. Someone who was here before
 * unlock keeps this layout through it (the button doesn't move under their
 * finger); someone who arrives after unlock gets the short "it's unlocked"
 * screen with the same button. Mirrors monolith lines 1339–1526.
 */
export interface NotStartedScheduledStateProps {
  scheduledTime: string;
  previousArt?: ArtInfo;
  artUrl: string | null;
  streakIcon: URL | null;
  isAuthenticated: boolean;
  currentUsername: string | null;
  onRetry: () => void;
  onTriggerWayback: () => void | Promise<void>;
  /** The viewer's no-emoji penalty: warned about before they tap. */
  emojiPenalty?: EmojiPenaltyResponse;
  /** Server-synced clock (useRaceClock): when the race button turns on. */
  clock: RaceClock;
  /** The race button. */
  onTap: (tapStartedAt: number) => void;
  /** A race request is on its way. */
  tapBusy?: boolean;
}

export const NotStartedScheduledState: React.FC<NotStartedScheduledStateProps> = ({
  scheduledTime,
  previousArt,
  artUrl,
  streakIcon,
  isAuthenticated,
  currentUsername,
  onTriggerWayback,
  emojiPenalty,
  clock,
  onTap,
  tapBusy,
}) => {
  // Server clock when synced; the device clock until the first sync answers.
  const isBeforeScheduledTime =
    clock.msToUnlock !== null ? clock.msToUnlock > 0 : new Date() < new Date(scheduledTime);
  const sawBeforeUnlockRef = useRef(false);
  if (isBeforeScheduledTime) sawBeforeUnlockRef.current = true;

  // Members who were here before unlock keep this layout; visitors always get
  // the "sign in to race" screen once it's unlocked.
  if (!isBeforeScheduledTime && (!sawBeforeUnlockRef.current || !isAuthenticated)) {
    if (!isAuthenticated) {
      // Signed-out visitors can't race. The page follows the day on its own
      // (useAotdRace re-reads /today on race activity), so the art shows up
      // here once someone claims it.
      return (
        <SignupMessage
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <div>It&apos;s unlocked. Sign in to race for today&apos;s art.</div>
          <div style={{ marginTop: "8px", fontSize: "0.9rem" }}>
            <Link to="/login" style={{ marginRight: "10px" }}>
              Log In
            </Link>
            <Link to="/register">Sign Up</Link>
          </div>
          <div style={{ marginTop: "10px", fontSize: "0.8rem", fontWeight: 400, color: "#666" }}>
            Or stay here: you&apos;ll see it painted as soon as someone claims it.
          </div>
        </SignupMessage>
      );
    }
    return (
      <SignupMessage
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <div>It&apos;s unlocked. Tap to race for today&apos;s art.</div>
        <RaceButton clock={clock} canRace onTap={onTap} busy={tapBusy} />
        <EmojiPenaltyNote penalty={emojiPenalty} moment="upcoming" />
      </SignupMessage>
    );
  }

  return (
    <div className="art-of-the-day">
      <AotdShiftBanner scheduledTime={scheduledTime} />
      <RaceButton clock={clock} canRace={isAuthenticated} onTap={onTap} busy={tapBusy} />
      <EmojiPenaltyNote penalty={emojiPenalty} moment="upcoming" />
      <AotdCardsReadyButton />
      {artUrl && previousArt ? (
        <AotdArtCard
          artInfo={previousArt}
          artUrl={artUrl}
          streakIcon={streakIcon}
          hasPermission={false}
          isAuthenticated={isAuthenticated}
          currentUsername={currentUsername}
          onTriggerWayback={onTriggerWayback}
          hideNextEmojis
          readOnly
        />
      ) : (
        <SignupMessage style={{ textAlign: "center", marginTop: "1rem" }}>
          <div style={{ fontSize: "0.85rem", color: "#666" }}>
            Check back later to create today&apos;s art.
          </div>
        </SignupMessage>
      )}
    </div>
  );
};

export default NotStartedScheduledState;
